<template>
    <v-layout
        row
        class="search-dropdown"
        v-if="showDropdown"
        @keyup.up="moveUp"
        @keyup.down="moveDown"
        @focus="disableScroll"
        @blur="enableScroll"
        @keydown.enter="applyNavigation"
        @keydown.esc="closeDropdown"
        @keydown="delegateFocus"
    >
        <v-flex>
            <v-card>
                <last-viewed-products v-if="!query"></last-viewed-products>
                <v-layout
                    row
                    v-else-if="totalFound > 0"
                >
                    <v-flex>
                        <v-card flat>
                            <v-list
                                two-line
                                align-self-center
                                class="px-1"
                                light
                            >
                                <v-list-tile>
                                    <v-list-tile-content>
                                        <template v-if="totalProductsFound < 1 
                                    && ((categories != undefined && categories.length > 0) 
                                    || (merchants != undefined && Object.values(merchants).length > 0))">
                                            <v-list-tile-title>{{ 'No results for' | translation }} "{{ query }}"</v-list-tile-title>
                                        </template>
                                        <template v-else>
                                            <v-list-tile-title>{{ 'Press Enter to' | translation }}</v-list-tile-title>
                                            <v-list-tile-sub-title>{{ totalProductsFound.toLocaleString() }} Ergebnisse anzuzeigen</v-list-tile-sub-title>
                                        </template>
                                    </v-list-tile-content>
                                </v-list-tile>
                                <v-list
                                    two-line
                                    subheader
                                    dense
                                    v-if="categories != undefined && categories.length > 0"
                                >
                                    <v-subheader>{{ 'Categories' | translation }}</v-subheader>

                                    <v-list-tile
                                        v-for="category in categories"
                                        :key="category.id"
                                        :to="getRouteParams({category: category.id})"
                                    >
                                        <v-list-tile-content>
                                            <v-list-tile-title v-html="getResultString(category.title)">
                                            </v-list-tile-title>
                                        </v-list-tile-content>
                                    </v-list-tile>
                                    <v-divider></v-divider>
                                </v-list>
                                <v-list
                                    two-line
                                    subheader
                                    dense
                                    v-if="merchants != undefined && Object.values(merchants).length > 0"
                                >
                                    <v-subheader>{{ 'dealer' | translation }}</v-subheader>

                                    <v-list-tile
                                        v-for="merchant in merchants"
                                        :key="merchant.value"
                                        :href="merchant.products_count > 0 ? `/${merchant.uri}/search` : `/${merchant.uri}`"
                                    >
                                        <v-list-tile-content>
                                            <v-list-tile-title v-html="getResultString(merchant.name)">
                                            </v-list-tile-title>
                                        </v-list-tile-content>
                                    </v-list-tile>
                                </v-list>
                            </v-list>
                        </v-card>
                    </v-flex>
                </v-layout>
                <v-layout
                    row
                    v-else-if="totalProductsFound === 0"
                >
                    <v-flex>
                        <v-card flat>
                            <v-list
                                three-line
                                align-self-center
                                class="px-1"
                                light
                            >
                                <v-list-tile>
                                    <v-list-tile-content>
                                        <v-list-tile-title>
                                            {{ 'No results for' | translation }} "{{ query }}" {{ 'proposals' | translation }}
                                        </v-list-tile-title>
                                        <v-list-tile-sub-title>
                                            {{ 'Check if all words are spelled correctly' | translation }}
                                        </v-list-tile-sub-title>
                                        <v-list-tile-sub-title>
                                            {{ 'Try another search term' | translation }}
                                        </v-list-tile-sub-title>
                                        <v-list-tile-sub-title>
                                            {{ 'Try a more general search term' | translation }}
                                        </v-list-tile-sub-title>
                                    </v-list-tile-content>
                                </v-list-tile>
                            </v-list>
                        </v-card>
                    </v-flex>
                </v-layout>
            </v-card>
        </v-flex>
    </v-layout>
</template>

<script>
import LastViewedProducts from './LastViewedProducts';
import module from './store/searchDropdown';
import { mapState } from 'vuex';

const moduleName = 'SearchDropdown';
const searchResultsModuleName = 'SearchResults';

export default {
    name: 'SearchDropdown',
    searchDropdown: null,
    components: {
        LastViewedProducts,
    },
    props: ['query'],
    data() {
        return {
            opened: false,
        };
    },
    beforeCreate() {
        if (!this.$store.state[moduleName]) {
            this.$store.registerModule(moduleName, module);
        }
    },
    computed: {
        ...mapState(searchResultsModuleName, {
            products: state => state.products,
            lastSearchParams: state => state.lastSearchParams,
        }),
        ...mapState(moduleName, {
            categoriesFound: state => state.categoriesFound,
            merchantsFound: state => state.merchantsFound,
            totalFound: state => state.totalFound,
        }),
        categories: function() {
            return this.categoriesFound.data;
        },
        merchants: function() {
            return this.merchantsFound.data;
        },
        totalProductsFound: function() {
            return this.products.total;
        },
        showDropdown: function() {
            return (
                this.opened === true &&
                (this.totalFound > 0 ||
                    !this.query ||
                    (this.totalProductsFound === 0 && this.query === this.lastSearchParams.query))
            );
        },
    },
    methods: {
        moveDown: function(e) {
            let element = this.getActiveItem(e.target);
            let next;
            if (element && element.classList.contains('active')) {
                next = this.getNextActiveItem(element);
            } else {
                next = element;
            }

            if (next) {
                element.classList.remove('active');
                next.classList.add('active');
            }
        },
        moveUp: function(e) {
            let element = this.getActiveItem(e.target);
            let prev = this.getPrevActiveItem(element);
            if (prev) {
                element.classList.remove('active');
                prev.classList.add('active');
            } else {
                if (element) {
                    element.classList.remove('active');
                }
                this.delegateFocus();
            }
        },
        getNextActiveItem: function(currentItem) {
            if (currentItem) {
                let next = currentItem.nextElementSibling;
                if (next) {
                    if (!next.classList.contains('list-item')) {
                        return this.getNextActiveItem(next);
                    } else {
                        return next;
                    }
                }
            }
        },
        getPrevActiveItem: function(currentItem) {
            if (currentItem) {
                let prev = currentItem.previousElementSibling;
                if (prev) {
                    if (!prev.classList.contains('list-item')) {
                        return this.getPrevActiveItem(prev);
                    } else {
                        return prev;
                    }
                }
            }
        },
        getActiveItem: function(parent) {
            if (parent === undefined) {
                parent = this.$el;
            }
            if (!parent.classList.contains('list-container')) {
                let containers = parent.querySelectorAll('.list-container');
                if (containers.length > 0) {
                    parent = containers[0];
                } else {
                    return false;
                }
            }

            let items = parent.querySelectorAll('.active');
            if (items.length > 0) {
                return items[0];
            } else {
                let items = parent.querySelectorAll('.list-item');
                if (items.length > 0) {
                    return items[0];
                }
            }
        },
        applyNavigation: function(e) {
            let active = this.getActiveItem(e.target);
            let link;
            if (active && active.tagName != 'A') {
                let links = active.querySelectorAll('a');
                if (links.length > 0) {
                    link = links[0];
                }
            } else if (active) {
                link = active;
            }
            if (link) {
                link.click();
            }
        },
        disableScroll: function() {
            document.addEventListener('keydown', this.preventEvent);
        },
        enableScroll: function() {
            document.removeEventListener('keydown', this.preventEvent);
        },
        preventEvent: function(event) {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
            }
        },
        getRouteParams: function(additionalParams, reset = true) {
            return this.$search.buildSearchParams(additionalParams, reset);
        },
        delegateFocus: function(e) {
            if (
                e &&
                e.key != 'ArrowDown' &&
                e.key != 'ArrowUp' &&
                e.key != 'Enter'
            ) {
                this.$parent.$el.focus();
            } else if (!e) {
                this.$parent.$el.focus();
            }
        },
        selfFocus: function() {
            this.$el.focus();
        },
        openDropdown: function() {
            this.opened = true;
        },
        closeDropdown: function(e) {
            if (e && (e.type == 'click' || e.type == 'mousedown')) {
                if (
                    (!this.$root.getParentByClassName(
                        e.target,
                        'search-dropdown'
                    ) &&
                        !this.$root.getParentByClassName(
                            e.target,
                            'search-box'
                        )) ||
                    (this.$root.getParentByTagName(e.target, 'a') &&
                        e.button != 2)
                ) {
                    this.opened = false;
                }
            } else {
                this.opened = false;
            }
        },
        getResultString: function(string) {
            let found = new RegExp('(' + this.query + ')', 'gi');
            return string.replace(
                found,
                '<span class="font-weight-bold">$1</span>'
            );
        }
    },
    watch: {
        showDropdown(val) {
            if (val) {
                document.addEventListener('click', this.closeDropdown);
            } else {
                document.removeEventListener('click', this.closeDropdown);
            }
        },
    },
};
</script>
